/**
 * Canonical public register wizard steps (Figma: Trial & Onboarding / Current).
 * Steps 1–4 = pre-submit wizard. Steps 5–6 = /register/status/:publicId.
 */

export const PRE_SUBMIT_STEPS = ['modules', 'company', 'tenant', 'review'];

export const STEP_META = {
  modules: { index: 1, total: 6 },
  company: { index: 2, total: 6 },
  tenant: { index: 3, total: 6 },
  review: { index: 4, total: 6 },
  provisioning: { index: 5, total: 6 },
  done: { index: 6, total: 6 },
};

/** Figma user tiers — map to users_count for backend pricing. */
export const USER_TIER_OPTIONS = [
  { id: '1-10', min: 1, max: 10, usersCount: 10, labelKey: 'user_tier_1_10' },
  { id: '11-50', min: 11, max: 50, usersCount: 50, labelKey: 'user_tier_11_50' },
  { id: '51-100', min: 51, max: 100, usersCount: 100, labelKey: 'user_tier_51_100' },
  { id: '101+', min: 101, max: null, usersCount: 101, labelKey: 'user_tier_101_plus' },
];

export const DRAFT_STORAGE_KEY = 'mafert_public_register_wizard_draft_v1';

/** Per-launch snapshot used by steps 5–6 to render what was ordered. */
export const LAUNCH_CONTEXT_KEY_PREFIX = 'mafert_public_register_launch_v1_';

/** Ordered checklist shown on step 4 before the single trial POST. */
export const READY_CHECKLIST_IDS = ['modules', 'company', 'tenant', 'consent', 'pricing'];

/** Required billing customer type chosen on step 2 (no default). */
export const CUSTOMER_TYPES = ['business', 'consumer'];

export function isBusinessCustomer(form) {
  return form?.customer_type === 'business';
}

export function isConsumerCustomer(form) {
  return form?.customer_type === 'consumer';
}

/**
 * Name used for the workspace slug preview: company name for business,
 * company name or (fallback) contact name for consumer.
 */
export function resolveSlugSourceName(form) {
  const company = String(form?.company_name || '').trim();
  if (isBusinessCustomer(form)) {
    return company;
  }
  if (isConsumerCustomer(form)) {
    return company || String(form?.contact_name || '').trim();
  }
  return '';
}

/**
 * Customer-type specific part of the POST /trial payload.
 * Consumers never send company identifiers; company_name is optional for them.
 */
export function buildCustomerTypePayload(form) {
  const type = form?.customer_type;
  const payload = { customer_type: type };
  const companyName = String(form?.company_name || '').trim();

  if (type === 'business') {
    payload.company_name = companyName;
    ['company_id_number', 'tax_id', 'vat_id'].forEach((key) => {
      const trimmed = String(form?.[key] || '').trim();
      if (trimmed) {
        payload[key] = trimmed;
      }
    });
  } else if (companyName) {
    payload.company_name = companyName;
  }

  return payload;
}

/**
 * Customer portion of the public /register POST. Admin trial must send the
 * same names. Tenant, modules and billing stay with the caller.
 */
export function buildRegisterCustomerPayload(form, { selectedCountry, registerOptions } = {}) {
  const payload = {
    ...buildCustomerTypePayload(form),
    contact_name: form?.contact_name,
    contact_email: form?.contact_email,
    country_code: form?.country_code,
    country_name: form?.country_name || selectedCountry?.name,
    locale: form?.locale || registerOptions?.default_locale,
    timezone: form?.timezone || registerOptions?.default_timezone,
    billing_currency: form?.billing_currency || registerOptions?.default_billing_currency,
  };

  const phoneNational = String(form?.phone_national_number || '').trim();
  if (phoneNational) {
    payload.phone_country_code = form.phone_country_code;
    payload.phone_national_number = phoneNational;
  }

  const notes = String(form?.notes || '').trim();
  if (notes) {
    payload.notes = notes;
  }

  ['address_line1', 'city', 'postal_code'].forEach((key) => {
    const trimmed = String(form?.[key] || '').trim();
    if (trimmed) {
      payload[key] = trimmed;
    }
  });

  return payload;
}

export function parseStepParam(value) {
  const raw = String(value || '').toLowerCase();
  if (PRE_SUBMIT_STEPS.includes(raw)) {
    return raw;
  }
  return null;
}

/**
 * Public register `billing` query.
 * Only "yearly" selects the yearly period. A missing, blank, or unknown
 * value uses the safe monthly default. Callers that must keep a saved draft
 * should skip this when the query parameter itself is absent.
 */
export function resolveRegisterBillingInterval(raw) {
  if (String(raw ?? '').trim().toLowerCase() === 'yearly') {
    return 'yearly';
  }
  return 'monthly';
}

export function resolveTierFromUsersCount(usersCount) {
  const n = Number(usersCount) || 10;
  const match = USER_TIER_OPTIONS.find((tier) => {
    if (tier.max == null) {
      return n >= tier.min;
    }
    return n >= tier.min && n <= tier.max;
  });
  return match?.id || '1-10';
}

export function usersCountForTier(tierId) {
  const tier = USER_TIER_OPTIONS.find((item) => item.id === tierId);
  return tier?.usersCount ?? 10;
}

export function wizardProgressPercent(stepKey) {
  const meta = STEP_META[stepKey];
  if (!meta) {
    return 0;
  }
  return Math.round((meta.index / meta.total) * 100);
}

/**
 * Earliest incomplete pre-submit step for URL bypass protection.
 * @param {{ selectedModules: string[], form: object, consentAccepted: boolean, tenantSlug: string }} state
 */
export function earliestIncompleteStep(state) {
  if (!state?.selectedModules?.length) {
    return 'modules';
  }
  const form = state.form || {};
  if (!CUSTOMER_TYPES.includes(form.customer_type)
    || (isBusinessCustomer(form) && !String(form.company_name || '').trim())
    || !String(form.contact_name || '').trim()
    || !String(form.contact_email || '').trim()
    || !state.consentAccepted) {
    return 'company';
  }
  const slug = String(form.tenant_slug || state.tenantSlug || '').trim();
  if (!slug || !/^[a-z0-9-]+$/.test(slug)) {
    return 'tenant';
  }
  return 'review';
}

export function validateModulesStep({ selectedModules }) {
  const errors = {};
  if (!selectedModules?.length) {
    errors.modules = 'modules_required';
  }
  return errors;
}

export function validateCompanyStep({ form, consentAccepted, phoneInvalid }) {
  const errors = {};
  if (!CUSTOMER_TYPES.includes(form?.customer_type)) {
    errors.customer_type = 'required';
  }
  if (form?.customer_type === 'business' && !String(form?.company_name || '').trim()) {
    errors.company_name = 'required';
  }
  if (!String(form?.contact_name || '').trim()) {
    errors.contact_name = 'required';
  }
  if (!String(form?.contact_email || '').trim()) {
    errors.contact_email = 'required';
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(form.contact_email).trim())) {
    errors.contact_email = 'invalid_email';
  }
  if (phoneInvalid) {
    errors.phone_national_number = 'invalid_phone';
  }
  if (!consentAccepted) {
    errors.consent = 'required';
  }
  return errors;
}

export function validateTenantStep({ form }) {
  const errors = {};
  const slug = String(form?.tenant_slug || '').trim();
  if (!slug) {
    errors.tenant_slug = 'required';
  } else if (!/^[a-z0-9-]+$/.test(slug)) {
    errors.tenant_slug = 'invalid_format';
  }
  return errors;
}

/**
 * Step 4 readiness checklist. Mirrors the pre-submit validators so the review
 * screen never enables submit for a state the wizard itself would reject.
 * @returns {{ id: string, done: boolean }[]}
 */
export function buildReadyChecklist({
  selectedModules,
  form,
  consentAccepted,
  pricingReady,
} = {}) {
  const companyErrors = validateCompanyStep({
    form: form || {},
    consentAccepted: true,
    phoneInvalid: false,
  });
  const tenantErrors = validateTenantStep({ form: form || {} });

  return [
    { id: 'modules', done: (selectedModules || []).length > 0 },
    { id: 'company', done: Object.keys(companyErrors).length === 0 },
    { id: 'tenant', done: Object.keys(tenantErrors).length === 0 },
    { id: 'consent', done: consentAccepted === true },
    { id: 'pricing', done: pricingReady === true },
  ];
}

export function isReadyChecklistComplete(checklist) {
  return (checklist || []).every((item) => item.done === true);
}

export function canSubmitRegistration({ checklist, confirmationAccepted, submitting } = {}) {
  return isReadyChecklistComplete(checklist)
    && confirmationAccepted === true
    && submitting !== true;
}

export function saveLaunchContext(publicId, context) {
  if (!publicId || typeof sessionStorage === 'undefined') {
    return;
  }
  try {
    sessionStorage.setItem(
      `${LAUNCH_CONTEXT_KEY_PREFIX}${publicId}`,
      JSON.stringify({ ...context, savedAt: Date.now() }),
    );
  } catch {
    // Ignore quota / private mode.
  }
}

export function loadLaunchContext(publicId) {
  if (!publicId || typeof sessionStorage === 'undefined') {
    return null;
  }
  try {
    const raw = sessionStorage.getItem(`${LAUNCH_CONTEXT_KEY_PREFIX}${publicId}`);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function loadWizardDraft() {
  if (typeof sessionStorage === 'undefined') {
    return null;
  }
  try {
    const raw = sessionStorage.getItem(DRAFT_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function saveWizardDraft(draft) {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  try {
    sessionStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify({
      ...draft,
      savedAt: Date.now(),
    }));
  } catch {
    // Ignore quota / private mode.
  }
}

export function clearWizardDraft() {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  try {
    sessionStorage.removeItem(DRAFT_STORAGE_KEY);
  } catch {
    // ignore
  }
}

/** High-level live-log lines derived from public launch status (no secrets). */
export function buildProvisioningLogLines(statusPayload, t) {
  if (!statusPayload?.status) {
    return [];
  }
  const lines = [];
  const status = statusPayload.status;
  lines.push({
    id: `status-${status}`,
    level: status === 'failed' ? 'error' : 'info',
    text: t(`onboarding:register.launch_status.${status}`, status),
  });
  if (typeof statusPayload.progress_percent === 'number') {
    lines.push({
      id: 'progress',
      level: 'info',
      text: t('onboarding:register.wizard.live_log_progress', {
        percent: Math.round(statusPayload.progress_percent),
      }),
    });
  }
  const flags = [
    ['application_http_ready', 'live_log_app_http'],
    ['domain_dns_ready', 'live_log_dns'],
    ['domain_tls_ready', 'live_log_tls'],
    ['activation_delivery_ready', 'live_log_delivery'],
    ['admin_activated', 'live_log_admin'],
    ['public_ready', 'live_log_public_ready'],
  ];
  flags.forEach(([key, i18nKey]) => {
    if (statusPayload[key] === true) {
      lines.push({
        id: key,
        level: 'success',
        text: t(`onboarding:register.wizard.${i18nKey}`),
      });
    }
  });
  if (status === 'failed' && statusPayload.failure_code) {
    lines.push({
      id: 'failure_code',
      level: 'error',
      text: t('onboarding:register.wizard.live_log_failure_code', {
        code: statusPayload.failure_code,
      }),
    });
  }
  return lines;
}
